import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { resilientSnapshot } from '../utils/resilientSnapshot';
import { Send, X, MessageSquare, Camera, Trash2, Flag, EyeOff, Ban } from 'lucide-react';
import { pickAndCompressPhoto, uploadChatPhoto } from '../utils/chatPhoto';
import { isObjectionable } from '../utils/contentFilter';

// Local moderation state: photos/users the THIS device chose to hide (reporting
// auto-hides for the reporter; global takedown is the admin queue's job).
const HIDDEN_MSGS_KEY = 'bender_hidden_msgs';
const HIDDEN_USERS_KEY = 'bender_hidden_users';
const readList = (k) => { try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch { return []; } };
const addToList = (k, v) => { try { const l = readList(k); if (!l.includes(v)) { l.push(v); localStorage.setItem(k, JSON.stringify(l)); } } catch { /* ignore */ } };
const removeFromList = (k, v) => { try { localStorage.setItem(k, JSON.stringify(readList(k).filter(x => x !== v))); } catch { /* ignore */ } };

const SquadChat = ({ db, appId, squadCode, user, username, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isStandalone, setIsStandalone] = useState(false);
  const [sendingPhoto, setSendingPhoto] = useState(false);
  const [viewing, setViewing] = useState(null); // photo message open fullscreen
  const [hiddenMsgs, setHiddenMsgs] = useState(() => readList(HIDDEN_MSGS_KEY));
  const [hiddenUsers, setHiddenUsers] = useState(() => readList(HIDDEN_USERS_KEY));
  const scrollRef = useRef(null);

  useEffect(() => {
    // Detect Standalone Mode
    const inStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || window.Capacitor?.isNativePlatform?.();
    setIsStandalone(inStandalone);

    if (!squadCode) return;

    // Path: artifacts/{appId}/public/data/squads/{squadCode}/messages
    const msgRef = collection(db, 'artifacts', appId, 'public', 'data', 'squads', squadCode, 'messages');
    const q = query(msgRef, orderBy('timestamp', 'asc'));

    const unsubscribe = resilientSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
      // Auto-scroll to bottom
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });

    return () => unsubscribe();
  }, [squadCode, db, appId]);

  // Photo flow is two-step on purpose: picking STAGES the photo as a preview
  // next to the input (nothing uploads yet), and only Send commits it — so a
  // wrong pick is a free ✕, and typed text rides along as the caption.
  const [pendingPhoto, setPendingPhoto] = useState(null); // { blob, previewUrl }

  const stagePhoto = async () => {
    if (sendingPhoto || pendingPhoto || !user) return;
    try {
      const staged = await pickAndCompressPhoto();
      if (staged) setPendingPhoto(staged);
    } catch (err) {
      console.error('Photo pick failed', err);
      alert('Could not load that photo. Try another one.');
    }
  };

  const discardPhoto = () => {
    if (pendingPhoto?.previewUrl) URL.revokeObjectURL(pendingPhoto.previewUrl);
    setPendingPhoto(null);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text && !pendingPhoto) return;
    if (sendingPhoto) return;

    // First-line automated filter (guideline 1.2). Refuse outright — no
    // auto-editing — so nothing objectionable ever reaches the squad.
    if (isObjectionable(text)) {
      alert('Message blocked: that violates the zero-tolerance content policy.');
      return;
    }

    const msgRef = collection(db, 'artifacts', appId, 'public', 'data', 'squads', squadCode, 'messages');
    try {
      if (pendingPhoto) {
        setSendingPhoto(true);
        const imageUrl = await uploadChatPhoto({ squadId: squadCode, uid: user.uid, blob: pendingPhoto.blob });
        await addDoc(msgRef, {
          imageUrl,
          // Typed text becomes the caption; the fallback keeps pre-photo
          // clients (iOS <= 1.1.1) from rendering an empty bubble.
          text: text || '📷 Photo',
          senderId: user.uid,
          senderName: username || "Rogue Agent",
          timestamp: serverTimestamp()
        });
        discardPhoto();
      } else {
        await addDoc(msgRef, {
          text,
          senderId: user.uid,
          senderName: username || "Rogue Agent",
          timestamp: serverTimestamp()
        });
      }
      setNewMessage('');
    } catch (err) {
      console.error('Send failed', err);
      alert('Could not send. Check connection and try again.');
    } finally {
      setSendingPhoto(false);
    }
  };

  const deleteOwnPhoto = async (msg) => {
    if (!window.confirm('Delete this photo for everyone?')) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'squads', squadCode, 'messages', msg.id));
      setViewing(null);
    } catch (e) { console.error('Delete failed', e); }
  };

  // Flag any message — photo or text (guideline 1.2 requires flagging on all
  // UGC, not just images). Hides it for the reporter immediately; the report
  // lands in the admin moderation queue.
  const reportMessage = async (msg) => {
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'reports'), {
        type: msg.imageUrl ? 'chat_photo' : 'chat_text',
        squadId: squadCode,
        messageId: msg.id,
        imageUrl: msg.imageUrl || null,
        text: msg.text || null,
        senderId: msg.senderId,
        senderName: msg.senderName || null,
        reportedBy: user.uid,
        createdAt: serverTimestamp(),
      });
    } catch (e) { console.error('Report failed', e); }
    // Hide it for the reporter immediately, regardless of the write outcome.
    addToList(HIDDEN_MSGS_KEY, msg.id);
    setHiddenMsgs(readList(HIDDEN_MSGS_KEY));
    setViewing(null);
    setActionMsg(null);
    alert('Reported. It is hidden for you and will be reviewed within 24 hours.');
  };

  // Block a user: their content disappears from this device instantly AND the
  // developer is notified with the triggering content (guideline 1.2 wording:
  // "blocking should also notify the developer of the inappropriate content").
  const blockUser = async (msg) => {
    addToList(HIDDEN_USERS_KEY, msg.senderId);
    setHiddenUsers(readList(HIDDEN_USERS_KEY));
    setViewing(null);
    setActionMsg(null);
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'blocks'), {
        blockedUid: msg.senderId,
        blockedName: msg.senderName || null,
        squadId: squadCode,
        messageId: msg.id,
        imageUrl: msg.imageUrl || null,
        text: msg.text || null,
        blockedBy: user.uid,
        createdAt: serverTimestamp(),
      });
    } catch (e) { console.error('Block notify failed', e); }
  };

  const unhideUser = (uid) => {
    removeFromList(HIDDEN_USERS_KEY, uid);
    setHiddenUsers(readList(HIDDEN_USERS_KEY));
  };

  // Action sheet target for text messages (photos get theirs in the viewer).
  const [actionMsg, setActionMsg] = useState(null);
  const [showHiddenPanel, setShowHiddenPanel] = useState(false);
  // Names for the manage-hidden list, best-effort from loaded messages.
  const hiddenWithNames = hiddenUsers.map(uid => ({
    uid,
    name: messages.find(m => m.senderId === uid)?.senderName || `User ${uid.slice(0, 6)}…`,
  }));

  // Photos are ephemeral: after 24h the image disappears from chat (the file is
  // also deleted by a bucket lifecycle rule). A caption lives on as plain text.
  const PHOTO_TTL_MS = 24 * 3600 * 1000;
  const isPhotoExpired = (m) => {
    if (!m.imageUrl) return false;
    const t = m.timestamp?.toMillis ? m.timestamp.toMillis() : null;
    return t != null && (Date.now() - t) > PHOTO_TTL_MS;
  };

  const visible = messages
    .filter(m => !hiddenMsgs.includes(m.id) && !hiddenUsers.includes(m.senderId))
    // expired photo with no real caption → drop entirely
    .filter(m => !(isPhotoExpired(m) && (!m.text || m.text === '📷 Photo')));

  return (
    <div className="absolute inset-0 z-[4000] bg-slate-950/95 flex flex-col animate-in fade-in slide-in-from-bottom-5">
      {/* Header - CHANGED: Dynamic Padding for PWA */}
      {/* Top padding must ALWAYS clear the notch/Dynamic Island. Uses a Tailwind
          arbitrary value (not the .pt-safe helper) because .pt-safe lives in the
          index.html <style> and loses to Tailwind's `p-4` on load order. */}
      <div className="border-b border-slate-800 flex justify-between items-center bg-slate-900 transition-all px-4 pb-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)]">
        <div className="flex items-center gap-2">
          <MessageSquare className="text-emerald-400" size={20} />
          <h2 className="text-lg font-black italic text-white tracking-tighter uppercase">Squad Comms: {squadCode}</h2>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><X size={24} /></button>
      </div>

      {/* Hidden-users bar — undo path for a mistaken "Hide this user" */}
      {hiddenUsers.length > 0 && (
        <button onClick={() => setShowHiddenPanel(true)}
          className="flex items-center justify-center gap-2 py-1.5 bg-slate-900/80 border-b border-slate-800 text-[9px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-300">
          <EyeOff size={11} /> {hiddenUsers.length} blocked {hiddenUsers.length === 1 ? 'user' : 'users'} · Manage
        </button>
      )}

      {/* Message Area */}
      <div className="flex-grow overflow-y-auto p-4 space-y-4 no-scrollbar">
        {visible.map((msg) => (
          <div key={msg.id} className={`flex flex-col ${msg.senderId === user.uid ? 'items-end' : 'items-start'}`}>
            <span className="text-[8px] font-black text-slate-500 uppercase mb-1">{msg.senderName}</span>
            {msg.imageUrl && !isPhotoExpired(msg) ? (
              <button
                onClick={() => setViewing(msg)}
                className={`max-w-[80%] rounded-2xl overflow-hidden shadow-lg border text-left ${
                  msg.senderId === user.uid ? 'border-emerald-700 rounded-tr-none' : 'border-slate-700 rounded-tl-none'
                }`}
              >
                <img src={msg.imageUrl} alt="Squad photo" className="block max-h-56 w-auto" loading="lazy" />
                {msg.text && msg.text !== '📷 Photo' && (
                  <div className={`px-3 py-1.5 text-xs font-bold ${msg.senderId === user.uid ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-200'}`}>
                    {msg.text}
                  </div>
                )}
              </button>
            ) : msg.senderId === user.uid ? (
              <div className="max-w-[80%] px-4 py-2 rounded-2xl text-sm font-bold shadow-lg bg-emerald-600 text-white rounded-tr-none">
                {msg.text}
              </div>
            ) : (
              // Others' text is tappable → report / block action sheet (1.2:
              // flagging + blocking must be reachable on every piece of UGC).
              <button
                onClick={() => setActionMsg(msg)}
                className="max-w-[80%] px-4 py-2 rounded-2xl text-sm font-bold shadow-lg text-left bg-slate-800 text-slate-200 rounded-tl-none"
              >
                {msg.text}
              </button>
            )}
          </div>
        ))}
        <div ref={scrollRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="pb-safe bg-slate-900 border-t border-slate-800">
        {/* Staged photo — picked but NOT sent until the send button. ✕ discards free. */}
        {pendingPhoto && (
          <div className="px-4 pt-3 flex items-center gap-3">
            <div className="relative">
              <img src={pendingPhoto.previewUrl} alt="Ready to send" className="h-16 w-16 object-cover rounded-lg border border-emerald-700" />
              {!sendingPhoto && (
                <button type="button" onClick={discardPhoto} aria-label="Discard photo"
                  className="absolute -top-2 -right-2 bg-slate-800 border border-slate-600 text-slate-300 rounded-full p-0.5">
                  <X size={14} />
                </button>
              )}
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              {sendingPhoto ? 'Uploading…' : 'Ready — add a caption or hit send'}
            </span>
          </div>
        )}
        <div className="p-4 flex gap-2">
          <button
            type="button"
            onClick={stagePhoto}
            disabled={sendingPhoto || !!pendingPhoto}
            className="bg-slate-800 border border-slate-700 text-emerald-400 p-3 rounded-xl active:scale-95 transition-transform disabled:opacity-50"
            aria-label="Attach a photo"
          >
            <Camera size={20} />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={pendingPhoto ? 'Add a caption…' : 'Drop a pin or message...'}
            className="flex-1 bg-black border border-slate-700 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-500 transition-colors"
          />
          <button type="submit" disabled={sendingPhoto}
            className="bg-emerald-500 text-white p-3 rounded-xl shadow-lg active:scale-95 transition-transform disabled:opacity-60">
            <Send size={20} className={sendingPhoto ? 'animate-pulse' : ''} />
          </button>
        </div>
      </form>

      {/* Manage hidden users — unhide by mistake-proofing */}
      {showHiddenPanel && (
        <div className="absolute inset-0 z-[5000] bg-black/90 flex items-center justify-center p-6 animate-in fade-in" onClick={() => setShowHiddenPanel(false)}>
          <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-black italic text-white uppercase tracking-tighter">Blocked users</h3>
              <button onClick={() => setShowHiddenPanel(false)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto no-scrollbar">
              {hiddenWithNames.map(({ uid, name }) => (
                <div key={uid} className="flex items-center justify-between bg-slate-800/60 border border-slate-700 rounded-xl px-3 py-2">
                  <span className="text-xs font-bold text-slate-200">{name}</span>
                  <button onClick={() => unhideUser(uid)}
                    className="text-[9px] font-black uppercase tracking-widest text-emerald-400 border border-emerald-700 px-2.5 py-1.5 rounded-lg active:scale-95">
                    Unblock
                  </button>
                </div>
              ))}
              {hiddenUsers.length === 0 && (
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-center py-2">Nobody blocked</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen photo viewer + actions (delete own / report + hide others) */}
      {viewing && (
        <div className="absolute inset-0 z-[5000] bg-black/95 flex flex-col animate-in fade-in" onClick={() => setViewing(null)}>
          <div className="flex justify-end px-4 pb-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)]">
            <button onClick={() => setViewing(null)} className="text-slate-400 hover:text-white"><X size={26} /></button>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center px-3" onClick={(e) => e.stopPropagation()}>
            <img src={viewing.imageUrl} alt="Squad photo" className="max-w-full max-h-full rounded-xl" />
          </div>
          <div className="px-4 pt-4 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] flex gap-2 justify-center" onClick={(e) => e.stopPropagation()}>
            {viewing.senderId === user.uid ? (
              <button onClick={() => deleteOwnPhoto(viewing)} className="flex items-center gap-2 bg-red-900/40 border border-red-800 text-red-300 font-black text-[10px] uppercase tracking-widest px-4 py-3 rounded-xl">
                <Trash2 size={14} /> Delete photo
              </button>
            ) : (
              <>
                <button onClick={() => reportMessage(viewing)} className="flex items-center gap-2 bg-red-900/40 border border-red-800 text-red-300 font-black text-[10px] uppercase tracking-widest px-4 py-3 rounded-xl">
                  <Flag size={14} /> Report
                </button>
                <button onClick={() => blockUser(viewing)} className="flex items-center gap-2 bg-slate-800 border border-slate-700 text-slate-300 font-black text-[10px] uppercase tracking-widest px-4 py-3 rounded-xl">
                  <Ban size={14} /> Block user
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Action sheet for someone else's TEXT message: report / block */}
      {actionMsg && (
        <div className="absolute inset-0 z-[5000] bg-black/80 flex flex-col justify-end animate-in fade-in" onClick={() => setActionMsg(null)}>
          <div className="bg-slate-900 border-t border-slate-700 rounded-t-3xl p-5 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)]" onClick={(e) => e.stopPropagation()}>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">{actionMsg.senderName}</p>
            <p className="text-xs font-bold text-slate-300 mb-4 line-clamp-2">{actionMsg.text}</p>
            <div className="space-y-2">
              <button onClick={() => reportMessage(actionMsg)}
                className="w-full flex items-center justify-center gap-2 bg-red-900/40 border border-red-800 text-red-300 font-black text-[10px] uppercase tracking-widest py-3.5 rounded-xl">
                <Flag size={14} /> Report message
              </button>
              <button onClick={() => blockUser(actionMsg)}
                className="w-full flex items-center justify-center gap-2 bg-slate-800 border border-slate-700 text-slate-300 font-black text-[10px] uppercase tracking-widest py-3.5 rounded-xl">
                <Ban size={14} /> Block {actionMsg.senderName || 'user'}
              </button>
              <button onClick={() => setActionMsg(null)}
                className="w-full text-slate-500 font-black text-[10px] uppercase tracking-widest py-3">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SquadChat;
